/**
 * Mode editor data — model registry + icon/effort choices, typed.
 *
 * Hand-mirror of `dashboard/lib/model-registry.js` (itself a mirror of
 * `orchestrator/lib/model-registry.js`) plus the choice constants and helpers
 * from `dashboard/views/index.ejs`. The control-plane web app is its own Vite
 * bundle with no orchestrator/dashboard dependency, so it can't import them.
 *
 * Adding a new model = update ALL THREE files (orchestrator, dashboard, here).
 * MODELS drives the mode editor's model dropdown; the orchestrator uses its
 * own copy for provider routing at chat-creation time.
 */

export type Provider = 'claude' | 'openai';

export interface ModelChoice {
  id: string;
  label: string;
  provider: Provider;
}

/** One provider's model + thinking config within a mode. */
export interface ModeSlot {
  model: string;
  thinking: { enabled: boolean; effort: string };
}

/** Canonical shape of a single mode (matches DEFAULT_MODES in settings-schema.ts). */
export interface Mode {
  id: string;
  title: string;
  description: string;
  icon: string;
  maxTurns: number;
  models: { openai?: ModeSlot; claude?: ModeSlot };
  // Legacy single-model fields, tolerated on READ only (pre-per-provider data
  // an operator hasn't re-saved). slotsForMode migrates them into `models`;
  // they are never written back.
  model?: string;
  thinking?: { enabled: boolean; effort: string };
}

// The `label` may name the provider — the control plane is the operator's
// admin view, like the dashboard. Plugin/mobile (end-user) surfaces must not.
export const MODELS: ModelChoice[] = [
  // Claude — fable-5 is natively 1M at the API, but the Claude Code CLI only
  // grants 1M treatment (window + autocompact ceiling) via the [1m] suffix.
  { id: 'claude-fable-5[1m]', label: 'Claude Fable 5 (1M context)', provider: 'claude' },
  { id: 'claude-opus-5-5[1m]', label: 'Claude Opus 5.5 (1M context)', provider: 'claude' },
  { id: 'claude-opus-5[1m]', label: 'Claude Opus 5 (1M context)', provider: 'claude' },
  { id: 'claude-opus-4-8[1m]', label: 'Claude Opus 4.8 (1M context)', provider: 'claude' },
  { id: 'claude-opus-4-7[1m]', label: 'Claude Opus 4.7 (1M context)', provider: 'claude' },
  { id: 'claude-opus-4-7', label: 'Claude Opus 4.7', provider: 'claude' },
  { id: 'claude-opus-4-6[1m]', label: 'Claude Opus 4.6 (1M context)', provider: 'claude' },
  { id: 'claude-opus-4-6', label: 'Claude Opus 4.6', provider: 'claude' },
  { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6', provider: 'claude' },
  // OpenAI
  { id: 'gpt-6-astra', label: 'GPT 6 Astra', provider: 'openai' },
  { id: 'gpt-6.1-sol', label: 'GPT 6.1 Sol', provider: 'openai' },
  { id: 'gpt-6-sol', label: 'GPT 6 Sol', provider: 'openai' },
  { id: 'gpt-5.6-sol', label: 'GPT 5.6 Sol', provider: 'openai' },
  { id: 'gpt-5.6-terra', label: 'GPT 5.6 Terra', provider: 'openai' },
  { id: 'gpt-5.5', label: 'GPT 5.5', provider: 'openai' },
  { id: 'gpt-5.5-pro', label: 'GPT 5.5 Pro', provider: 'openai' },
  { id: 'gpt-5.4', label: 'GPT 5.4', provider: 'openai' },
];

export const PROVIDER_LABELS: Record<Provider, string> = {
  claude: 'Claude',
  openai: 'OpenAI',
};

// Providers a mode carries a slot for, in stable order. Mirror of the
// orchestrator's PROVIDERS in settings.js.
export const PROVIDERS: Provider[] = ['openai', 'claude'];

/**
 * Per-provider default model id. Mirror of DEFAULT_MODEL_BY_PROVIDER in
 * `orchestrator/lib/model-registry.js`. Drives migration auto-fill of an absent
 * provider slot (so every mode works on both providers) + NEW_MODE seeding.
 */
export const DEFAULT_MODEL_BY_PROVIDER: Record<Provider, string> = {
  openai: 'gpt-6-sol',
  claude: 'claude-opus-5-5[1m]',
};

// Lucide icon names offered in the icon picker (PascalCase, matching
// lucide-react component names). Keep in sync with ICON_MAP in ModesEditor.tsx.
export const ICON_CHOICES = [
  'PenTool', 'Brain', 'BrainCircuit', 'Sparkles', 'Zap', 'Wand2', 'Lightbulb', 'Cpu',
  'Bot', 'Atom', 'Activity', 'Rocket', 'Target', 'Telescope', 'Flame', 'ScrollText',
  'Compass', 'Hourglass', 'Eye', 'Gauge', 'Crown', 'MessageSquare', 'Feather', 'Star',
] as const;

export type EffortChoice = [value: string, label: string];

// Effort tiers vary by provider. Claude has `max` (Opus / Fable) and `xhigh`
// (Opus 4.7+ / Fable; the orchestrator's resolveEffort caps it to `high`
// elsewhere). OpenAI's reasoning.effort adds `none`/`minimal`, and has `max`
// on GPT 6 / GPT 5.6 only (resolveEffort caps it to `high` elsewhere). The
// picker swaps when the model changes; clampEffort coerces any stale persisted
// value to the closest supported tier.
export const EFFORT_CHOICES_BY_PROVIDER: Record<Provider, EffortChoice[]> = {
  claude: [
    ['low', 'Low'], ['medium', 'Medium'], ['high', 'High'],
    ['xhigh', 'XHigh (Opus 4.7+ / Fable)'], ['max', 'Max (Opus / Fable)'],
  ],
  openai: [
    ['none', 'None'], ['minimal', 'Minimal'],
    ['low', 'Low'], ['medium', 'Medium'], ['high', 'High'], ['xhigh', 'XHigh'],
    ['max', 'Max (GPT 6 / GPT 5.6)'],
  ],
};

/** Look up the provider for a given model id, defaulting to claude for unknowns. */
export function providerForModel(modelId: string): Provider {
  return MODELS.find((m) => m.id === modelId)?.provider ?? 'claude';
}

/** Effort choices for a model's provider, with a claude fallback for unknowns. */
export function effortChoicesFor(modelId: string): EffortChoice[] {
  const choices = EFFORT_CHOICES_BY_PROVIDER[providerForModel(modelId)] ?? EFFORT_CHOICES_BY_PROVIDER.claude;
  return modelId === 'gpt-6.1-sol'
    ? choices.filter(([value]) => value !== 'none' && value !== 'minimal')
    : choices;
}

/**
 * Pick the closest supported effort tier for a model id. Mirrors the
 * orchestrator's resolveEffort coercion so the picker never shows a value
 * outside the provider's tier set (e.g. a saved Claude `max` switching to an
 * OpenAI model).
 */
export function clampEffort(effort: string, modelId: string): string {
  const allowed = new Set(effortChoicesFor(modelId).map(([v]) => v));
  if (allowed.has(effort)) return effort;
  if (modelId === 'gpt-6.1-sol' && (effort === 'none' || effort === 'minimal')) return 'low';
  if (providerForModel(modelId) === 'openai') return 'medium';
  if (effort === 'none' || effort === 'minimal') return 'low';
  return 'medium';
}

/** Stable client-side id for a freshly added mode (mode_<8 hex>). */
export function generateModeId(): string {
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  return 'mode_' + Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

// Normalize a present slot for the editor: keep its model (or the provider
// default if blank) and clamp its effort to that model's tier set.
function normalizeSlot(slot: Partial<ModeSlot> | undefined, provider: Provider): ModeSlot {
  const model = (typeof slot?.model === 'string' && slot.model) ? slot.model : DEFAULT_MODEL_BY_PROVIDER[provider];
  return {
    model,
    thinking: {
      enabled: slot?.thinking?.enabled ?? false,
      effort: clampEffort(slot?.thinking?.effort ?? 'medium', model),
    },
  };
}

/** An auto-filled slot for a provider (default model, thinking on). */
export function blankSlot(provider: Provider): ModeSlot {
  const model = DEFAULT_MODEL_BY_PROVIDER[provider];
  return { model, thinking: { enabled: true, effort: clampEffort('medium', model) } };
}

/**
 * Render-tolerant migration (TS mirror of the orchestrator's buildModelsMap):
 * return a both-slots map for a mode that may be new-shape (`models`), legacy
 * single-`model`, or neither — so the editor renders old data without forcing a
 * re-save. The editor writes back the new `models` shape on any edit.
 */
export function slotsForMode(mode: Mode): Record<Provider, ModeSlot> {
  let getSlot: (p: Provider) => Partial<ModeSlot> | undefined;
  if (mode.models && typeof mode.models === 'object' && (mode.models.openai || mode.models.claude)) {
    getSlot = (p) => mode.models[p];
  } else if (typeof mode.model === 'string' && mode.model) {
    const provider = providerForModel(mode.model);
    getSlot = (p) => (p === provider ? { model: mode.model, thinking: mode.thinking } : undefined);
  } else {
    getSlot = () => undefined;
  }
  const out = {} as Record<Provider, ModeSlot>;
  for (const p of PROVIDERS) {
    const slot = getSlot(p);
    out[p] = slot ? normalizeSlot(slot, p) : blankSlot(p);
  }
  return out;
}
